import { useTerminalsState } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { useNavigate } from "@/Utils/useNavigate";
import { IImportContext } from "@/components/ImportComponents/ImportContext";
import { ImportView } from "@/components/ImportComponents/ImportView";
import { Template } from "@/components/ImportComponents/types";
import { Page } from "@/components/Page";
import { useAccessParents } from "@/pages/infrastructure/functions";
import { useTrans } from "@/translations";

import React, { useCallback, useMemo } from "react";

import type { ACCESS_POINT } from "server/coreApi/accessPoints/types.js";

import { addToSystem } from "./addToSystem";
import { getImportSteps } from "./importSteps";
import { getTemplates } from "./templates";

export const ImportEvents: React.FC = () => {
  const { t } = useTrans();
  const navigate = useNavigate();
  const { currentTerminal } = useTerminalsState();

  const { data: customerList = [] } = trpc.customers.getAllMinimal.useQuery({
    terminalId: currentTerminal?.id,
  });

  const { accessParents } = useAccessParents();

  const trpcUtils = trpc.useUtils();

  const { mutateAsync: bulkCreateEvents } = trpc.events.bulkCreate.useMutation({
    onSuccess: () => trpcUtils.events.invalidate(),
  });

  const breadcrumbs = useMemo(
    () => [{ url: "/events", name: t("sidebarLabels:events") }],
    [t],
  );

  const containers = useMemo(() => {
    return accessParents
      .flatMap((ap) =>
        (ap.containers as ACCESS_POINT[]).map((c) => ({
          ...c,
          wasteRoom: ap,
        })),
      )
      .map((c) => ({
        ...c,
        name: c.name || c.wasteType.name,
      }));
  }, [accessParents]);

  const templates: Template[] = useMemo(() => {
    return getTemplates({
      customers: customerList,
      wasteTypes: containers,
      wasteRooms: accessParents,
    });
  }, [customerList, containers, accessParents]);

  const addToSystemFunction = useCallback(
    (context: IImportContext) =>
      addToSystem({
        context,
        bulkCreateEvents,
      }),
    [bulkCreateEvents],
  );

  const onCancel = useCallback(() => {
    navigate("/events");
  }, [navigate]);

  const importSteps = useMemo(() => getImportSteps(), []);

  return (
    <Page
      title="importLabels:importEvents"
      fullHeight
      breadcrumbsPaths={breadcrumbs}
    >
      <ImportView
        steps={importSteps}
        templates={templates}
        onCancel={onCancel}
        addToSystem={addToSystemFunction}
      />
    </Page>
  );
};
